/**
 * Vista 16 · Usuarios y permisos.
 *
 * `ARQ-10` llevado hasta el final: **el catálogo de roles no está cableado.**
 * El administrador crea los roles que el área necesite, los nombra y mueve
 * permisos marcando casillas. La matriz rol × permiso es DATO, no código, y
 * esta pantalla es su editor.
 *
 * Hay una sola regla que la configurabilidad no puede relajar, y la impone el
 * servidor: no se puede dejar el sistema sin ninguna cuenta activa capaz de
 * administrar permisos. Aquí eso se ve como un error al guardar, no como una
 * validación local: la comprobación tiene que hacerse contra la base.
 */
import { useId, useMemo, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
} from '@mpfn/ui'
import {
  Callout,
  DataTable,
  DatePicker,
  FormField,
  PageHeader,
  type DataTableColumn,
} from '@mpfn/patterns'
import { KeyRound, Plus } from 'lucide-react'
import {
  useActualizarRol,
  useActualizarUsuario,
  useCrearRol,
  useCrearUsuario,
  useExcepciones,
  useGuardarPermisosDeRol,
  useOtorgarExcepcion,
  useRestablecerPassword,
  useRoles,
  useUsuarios,
  type RolesYMatriz,
} from '../api/consultas.js'
import { useCatalogos } from '../api/catalogos.js'
import type { UsuarioAdmin } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Casilla, Interruptor } from '../componentes/Conmutadores.js'
import { ContrasenaTemporal } from '../componentes/ContrasenaTemporal.js'
import { DialogoConMotivo } from '../componentes/DialogoConMotivo.js'
import { useSesion } from '../sesion/contexto.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { fecha, hoy, instante } from '../lib/formato.js'

export function Usuarios() {
  const usuarios = useUsuarios()
  const roles = useRoles()

  const [creandoUsuario, setCreandoUsuario] = useState(false)
  const [detalle, setDetalle] = useState<UsuarioAdmin | undefined>(undefined)

  const columnas: ReadonlyArray<DataTableColumn<UsuarioAdmin>> = [
    { id: 'nombre', header: 'Nombre', cell: (fila) => fila.nombre },
    { id: 'correo', header: 'Correo', cell: (fila) => fila.correo, hideOnMobile: true },
    { id: 'rol', header: 'Rol', cell: (fila) => fila.rol },
    {
      id: 'estado',
      header: 'Estado',
      cell: (fila) =>
        !fila.activo ? (
          <StatusBadge tone="neutral" label="Desactivada" />
        ) : fila.requiereCambioPassword ? (
          <StatusBadge tone="warning" label="Contraseña temporal" />
        ) : (
          <StatusBadge tone="good" label="Activa" />
        ),
    },
    { id: 'creado', header: 'Creada', cell: (fila) => instante(fila.creadoEn), hideOnMobile: true },
  ]

  return (
    <Pagina>
      {/**
       * La cabecera va SIN acción, y las dos altas viven cada una en su card.
       *
       * Esta pantalla hace dos cosas de igual rango —administrar cuentas y
       * administrar roles— y las anunciaba como si una mandara sobre la otra:
       * «Nueva cuenta» arriba del todo, primario y grande; «Nuevo rol» más
       * abajo, secundario y blanco, dentro de su card. La jerarquía visual
       * decía algo que no es cierto.
       *
       * Ahora cada card lleva su propia alta, las dos con el mismo `Button`
       * primario en tamaño `sm`, y la cabecera de página solo titula.
       */}
      <PageHeader
        title="Usuarios y permisos"
        description="Las cuentas del sistema, los roles que existen y qué puede hacer cada uno."
      />

      <Card>
        <CardHeader className="sigfisc-seccion-card__cabecera">
          <CardTitle>Cuentas</CardTitle>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setCreandoUsuario(true)
            }}
          >
            <Plus size={14} aria-hidden="true" />
            Nueva cuenta
          </Button>
        </CardHeader>
        <CardContent>
          <Datos estado={usuarios}>
            {(lista) => (
              <DataTable
                columns={columnas}
                rows={lista}
                rowKey={(fila) => fila.id}
                onRowClick={setDetalle}
                rowActionLabel={(fila) => `Abrir la cuenta de ${fila.nombre}`}
                empty={{ title: 'Sin cuentas registradas' }}
              />
            )}
          </Datos>
        </CardContent>
      </Card>

      <Datos estado={roles}>{(datos) => <MatrizDeRoles datos={datos} />}</Datos>

      <DialogoNuevaCuenta
        abierto={creandoUsuario}
        onCambio={setCreandoUsuario}
        roles={roles.data?.roles ?? []}
      />

      {detalle && (
        <DetalleCuenta
          usuario={(usuarios.data ?? []).find((item) => item.id === detalle.id) ?? detalle}
          roles={roles.data?.roles ?? []}
          permisos={roles.data?.permisos ?? []}
          onCerrar={() => {
            setDetalle(undefined)
          }}
        />
      )}
    </Pagina>
  )
}

function DialogoNuevaCuenta({
  abierto,
  onCambio,
  roles,
}: {
  abierto: boolean
  onCambio: (valor: boolean) => void
  roles: RolesYMatriz['roles']
}) {
  const crear = useCrearUsuario()
  const idNombre = useId()
  const idCorreo = useId()
  const idRol = useId()

  const [nombre, setNombre] = useState('')
  const [correo, setCorreo] = useState('')
  const [rol, setRol] = useState('')
  const [temporal, setTemporal] = useState<string | undefined>(undefined)

  return (
    <Dialog
      open={abierto}
      onOpenChange={(valor) => {
        if (!valor) {
          setNombre('')
          setCorreo('')
          setRol('')
          setTemporal(undefined)
        }
        onCambio(valor)
      }}
    >
      <DialogContent>
        <DialogTitle>Nueva cuenta</DialogTitle>
        <DialogDescription>
          Nace con una contraseña temporal y no podrá hacer nada hasta cambiarla.
        </DialogDescription>

        {temporal !== undefined ? (
          <>
            <ContrasenaTemporal
              valor={temporal}
              nota="Se muestra una sola vez. Entréguesela a la persona por un medio seguro."
            />
            <div className="sigfisc-dialogo__acciones">
              <DialogClose asChild>
                <Button variant="primary">Ya la anoté</Button>
              </DialogClose>
            </div>
          </>
        ) : (
          <form
            className="sigfisc-formulario"
            onSubmit={(evento) => {
              evento.preventDefault()
              crear.mutate(
                { nombre: nombre.trim(), correo: correo.trim(), rol },
                {
                  onSuccess: (datos) => {
                    setTemporal(datos.passwordTemporal)
                    avisarExito('Cuenta creada.')
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo crear la cuenta')
                  },
                },
              )
            }}
          >
            <FormField id={idNombre} label="Nombre" required>
              <Input
                value={nombre}
                autoFocus
                onChange={(evento) => {
                  setNombre(evento.target.value)
                }}
              />
            </FormField>

            <FormField id={idCorreo} label="Correo institucional" required>
              <Input
                type="email"
                value={correo}
                onChange={(evento) => {
                  setCorreo(evento.target.value)
                }}
              />
            </FormField>

            <FormField id={idRol} label="Rol" required>
              <Select value={rol} onValueChange={setRol}>
                <SelectTrigger>
                  <SelectValue placeholder="Elija el rol" />
                </SelectTrigger>
                <SelectContent>
                  {roles
                    .filter((item) => item.activo)
                    .map((item) => (
                      <SelectItem key={item.codigo} value={item.codigo}>
                        {item.nombre}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FormField>

            <div className="sigfisc-dialogo__acciones">
              <DialogClose asChild>
                <Button variant="secondary" type="button">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="primary"
                type="submit"
                loading={crear.isPending}
                disabled={nombre.trim() === '' || correo.trim() === '' || rol === ''}
              >
                Crear la cuenta
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DetalleCuenta({
  usuario,
  roles,
  permisos,
  onCerrar,
}: {
  usuario: UsuarioAdmin
  roles: RolesYMatriz['roles']
  permisos: RolesYMatriz['permisos']
  onCerrar: () => void
}) {
  const sesion = useSesion()
  const actualizar = useActualizarUsuario()
  const restablecer = useRestablecerPassword()
  const excepciones = useExcepciones(usuario.id)
  const otorgar = useOtorgarExcepcion()

  const [restableciendo, setRestableciendo] = useState(false)
  const [desactivando, setDesactivando] = useState(false)
  const [temporal, setTemporal] = useState<string | undefined>(undefined)

  const [permiso, setPermiso] = useState('')
  const [otorgado, setOtorgado] = useState(true)
  const [desde, setDesde] = useState(hoy())
  const [hasta, setHasta] = useState('')
  const [motivo, setMotivo] = useState('')

  const esUnoMismo = usuario.id === sesion.usuario.id

  return (
    <Dialog
      open
      onOpenChange={(abierto) => {
        if (!abierto) onCerrar()
      }}
    >
      <DialogContent>
        <DialogTitle>{usuario.nombre}</DialogTitle>
        <DialogDescription>{usuario.correo}</DialogDescription>

        {temporal !== undefined && (
          <ContrasenaTemporal
            valor={temporal}
            nota="Se muestra una sola vez. Todas las sesiones de esta cuenta quedaron cerradas."
          />
        )}

        <div className="sigfisc-formulario">
          <FormField id="detalle-rol" label="Rol">
            <Select
              value={usuario.rol}
              onValueChange={(valor) => {
                actualizar.mutate(
                  { id: usuario.id, cambios: { rol: valor } },
                  {
                    onSuccess: () => {
                      avisarExito('Rol actualizado.')
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo cambiar el rol')
                    },
                  },
                )
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles
                  .filter((item) => item.activo)
                  .map((item) => (
                    <SelectItem key={item.codigo} value={item.codigo}>
                      {item.nombre}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </FormField>

          <Interruptor
            etiqueta="Cuenta activa"
            marcado={usuario.activo}
            deshabilitado={esUnoMismo}
            onCambio={() => {
              setDesactivando(true)
            }}
            {...(esUnoMismo ? { descripcion: 'No puede desactivar su propia cuenta.' } : {})}
          />

          <Button
            variant="destructive"
            onClick={() => {
              setRestableciendo(true)
            }}
          >
            <KeyRound size={16} aria-hidden="true" />
            Restablecer la contraseña
          </Button>
        </div>

        {/* ── Excepciones de permiso ──────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Excepciones de permiso</CardTitle>
          </CardHeader>
          <CardContent className="sigfisc-formulario">
            <p className="sigfisc-nota">
              Una excepción gana sobre el rol: sirve para otorgar algo puntual o para suspender a
              alguien sin cambiarle el rol.
            </p>

            <Datos estado={excepciones}>
              {(lista) =>
                lista.length === 0 ? (
                  <p className="sigfisc-nota">Sin excepciones vigentes.</p>
                ) : (
                  <ul className="sigfisc-items">
                    {lista.map((excepcion) => (
                      <li
                        key={`${excepcion.permiso}-${excepcion.vigenteDesde}`}
                        className="sigfisc-items__fila"
                      >
                        <span className="sigfisc-items__denominacion">
                          {excepcion.permiso}
                          <span className="sigfisc-items__marca">
                            {excepcion.otorgado ? 'otorgado' : 'revocado'}
                          </span>
                        </span>
                        <span className="sigfisc-items__verificado">
                          {fecha(excepcion.vigenteDesde)}
                          {excepcion.vigenteHasta !== undefined
                            ? ` – ${fecha(excepcion.vigenteHasta)}`
                            : ' en adelante'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }
            </Datos>

            <div className="sigfisc-fila-campos sigfisc-fila-campos--3">
              <FormField id="excepcion-permiso" label="Permiso" required>
                <Select value={permiso} onValueChange={setPermiso}>
                  <SelectTrigger>
                    <SelectValue placeholder="Elija el permiso" />
                  </SelectTrigger>
                  <SelectContent>
                    {permisos.map((item) => (
                      <SelectItem key={item.codigo} value={item.codigo}>
                        {item.codigo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField id="excepcion-desde" label="Vigente desde" required>
                <DatePicker
                  id="excepcion-desde"
                  value={desde}
                  onChange={setDesde}
                  yearRange={{ from: Number(hoy().slice(0, 4)), to: Number(hoy().slice(0, 4)) + 2 }}
                />
              </FormField>

              <FormField id="excepcion-hasta" label="Vigente hasta" hint="Vacío = sin límite.">
                <DatePicker
                  id="excepcion-hasta"
                  value={hasta}
                  onChange={setHasta}
                  yearRange={{ from: Number(hoy().slice(0, 4)), to: Number(hoy().slice(0, 4)) + 2 }}
                />
              </FormField>
            </div>

            <Interruptor
              etiqueta={otorgado ? 'Otorgar el permiso' : 'Revocar el permiso'}
              marcado={otorgado}
              onCambio={setOtorgado}
              descripcion="Una revocación explícita gana sobre el rol."
            />

            <FormField id="excepcion-motivo" label="Motivo" required hint="Al menos 10 caracteres.">
              <Textarea
                rows={2}
                value={motivo}
                onChange={(evento) => {
                  setMotivo(evento.target.value)
                }}
              />
            </FormField>

            <Button
              variant="secondary"
              loading={otorgar.isPending}
              disabled={permiso === '' || motivo.trim().length < 10}
              onClick={() => {
                otorgar.mutate(
                  {
                    usuarioId: usuario.id,
                    permiso,
                    otorgado,
                    vigenteDesde: desde,
                    ...(hasta ? { vigenteHasta: hasta } : {}),
                    motivo: motivo.trim(),
                  },
                  {
                    onSuccess: () => {
                      avisarExito('Excepción guardada.')
                      setPermiso('')
                      setMotivo('')
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo guardar la excepción')
                    },
                  },
                )
              }}
            >
              Guardar la excepción
            </Button>
          </CardContent>
        </Card>

        <div className="sigfisc-dialogo__acciones">
          <DialogClose asChild>
            <Button variant="secondary">Cerrar</Button>
          </DialogClose>
        </div>

        <DialogoConMotivo
          abierto={restableciendo}
          onCambio={setRestableciendo}
          titulo="Restablecer la contraseña"
          descripcion={`${usuario.nombre} · ${usuario.correo}`}
          etiquetaConfirmar="Restablecer y cerrar sus sesiones"
          cargando={restablecer.isPending}
          advertencia={
            <Callout tone="warning" title="Se cerrarán todas sus sesiones activas">
              La persona quedará fuera del sistema hasta que ingrese con la contraseña temporal y
              defina una nueva.
            </Callout>
          }
          onConfirmar={(razon) => {
            restablecer.mutate(
              { id: usuario.id, motivo: razon },
              {
                onSuccess: (datos) => {
                  setTemporal(datos.passwordTemporal)
                  setRestableciendo(false)
                  avisarExito('Contraseña restablecida.')
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo restablecer la contraseña')
                },
              },
            )
          }}
        />

        <DialogoConMotivo
          abierto={desactivando}
          onCambio={setDesactivando}
          titulo={usuario.activo ? 'Desactivar la cuenta' : 'Reactivar la cuenta'}
          descripcion={`${usuario.nombre} · ${usuario.correo}`}
          etiquetaConfirmar={usuario.activo ? 'Desactivar la cuenta' : 'Reactivar la cuenta'}
          cargando={actualizar.isPending}
          advertencia={
            usuario.activo ? (
              <Callout tone="warning" title="La cuenta no podrá ingresar">
                Sus sesiones vivas se cierran de inmediato. El historial de lo que hizo se conserva.
              </Callout>
            ) : undefined
          }
          onConfirmar={() => {
            actualizar.mutate(
              { id: usuario.id, cambios: { activo: !usuario.activo } },
              {
                onSuccess: () => {
                  avisarExito(usuario.activo ? 'Cuenta desactivada.' : 'Cuenta reactivada.')
                  setDesactivando(false)
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo actualizar la cuenta')
                },
              },
            )
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

function MatrizDeRoles({ datos }: { datos: RolesYMatriz }) {
  const catalogos = useCatalogos()
  const crearRol = useCrearRol()
  const actualizarRol = useActualizarRol()
  const guardarPermisos = useGuardarPermisosDeRol()

  const [rolActivo, setRolActivo] = useState(datos.roles[0]?.codigo ?? '')
  const [borrador, setBorrador] = useState<readonly string[] | undefined>(undefined)
  const [creando, setCreando] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [descripcion, setDescripcion] = useState('')

  const actuales = useMemo(
    () => borrador ?? datos.matriz[rolActivo] ?? [],
    [borrador, datos.matriz, rolActivo],
  )

  /**
   * Los permisos agrupados, **en el orden del catálogo** —que sigue el
   * recorrido del proceso— y no en el que llegaron de la base.
   *
   * Un grupo que no tenga ningún permiso no se emite: antes la rejilla
   * reservaba su hueco igual.
   */
  const porGrupo = useMemo(() => {
    const mapa = new Map<string, RolesYMatriz['permisos']>()
    for (const item of datos.permisos) {
      mapa.set(item.grupo, [...(mapa.get(item.grupo) ?? []), item])
    }
    return catalogos.gruposDePermiso
      .map((grupo) => ({ ...grupo, permisos: mapa.get(grupo.codigo) ?? [] }))
      .filter((grupo) => grupo.permisos.length > 0)
  }, [datos.permisos, catalogos.gruposDePermiso])

  const rol = datos.roles.find((item) => item.codigo === rolActivo)
  const sucio = borrador !== undefined

  return (
    <Card>
      <CardHeader className="sigfisc-seccion-card__cabecera">
        <CardTitle>Roles y matriz de permisos</CardTitle>
        {/* Primario, igual que el alta de cuentas: son dos acciones del mismo
            rango y deben pesar lo mismo en pantalla. */}
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setCreando(true)
          }}
        >
          <Plus size={14} aria-hidden="true" />
          Nuevo rol
        </Button>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <FormField id="rol-activo" label="Rol">
          <Select
            value={rolActivo}
            onValueChange={(valor) => {
              setRolActivo(valor)
              setBorrador(undefined)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Elija un rol" />
            </SelectTrigger>
            <SelectContent>
              {datos.roles.map((item) => (
                <SelectItem key={item.codigo} value={item.codigo}>
                  {item.nombre}
                  {!item.activo && ' · desactivado'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        {rol && (
          <>
            <p className="sigfisc-nota">{rol.descripcion}</p>

            <Interruptor
              etiqueta="Rol activo"
              marcado={rol.activo}
              descripcion="Un rol en uso no se borra, se desactiva: las cuentas conservan su historial."
              onCambio={(valor) => {
                actualizarRol.mutate(
                  { codigo: rol.codigo, cambios: { activo: valor } },
                  {
                    onSuccess: () => {
                      avisarExito(valor ? 'Rol reactivado.' : 'Rol desactivado.')
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo actualizar el rol')
                    },
                  },
                )
              }}
            />

            {/**
              * Lista en UNA columna, no rejilla.
              *
              * La rejilla de cuatro columnas igualaba las alturas de cada fila:
              * el grupo «Control interno», con un solo permiso, ocupaba un
              * bloque de 545 px. Apilados, cada grupo mide lo que pide su
              * contenido y el desalineamiento desaparece por construcción.
              *
              * Y lo que manda es la DESCRIPCIÓN, no el código: nadie puede
              * decidir si conceder `requerimiento.emitir_sobre_minimo` leyendo
              * ese identificador. El backend ya la servía y no se usaba.
              */}
            <div className="sigfisc-permisos">
              {porGrupo.map((grupo) => {
                const activos = grupo.permisos.filter((item) => actuales.includes(item.codigo)).length
                return (
                  <fieldset key={grupo.codigo} className="sigfisc-permisos__grupo">
                    <legend className="sigfisc-permisos__titulo">
                      <span>{grupo.etiqueta}</span>
                      <span className="sigfisc-permisos__contador">
                        {activos} de {grupo.permisos.length}
                      </span>
                    </legend>
                    {grupo.permisos.map((item) => (
                      /* `Casilla` y no un `<label>` propio: Radix monta el
                         control como `<button>`, y envolverlo en una etiqueta
                         NO lo asocia. Ese problema ya estaba resuelto ahí. */
                      <Casilla
                        key={item.codigo}
                        etiqueta={
                          <span className="sigfisc-permisos__texto">
                            <span className="sigfisc-permisos__nombre">
                              {item.descripcion === '' ? item.codigo : item.descripcion}
                            </span>
                            <code className="sigfisc-permisos__codigo">{item.codigo}</code>
                          </span>
                        }
                        marcado={actuales.includes(item.codigo)}
                        onCambio={(valor) => {
                          setBorrador(
                            valor
                              ? [...actuales, item.codigo]
                              : actuales.filter((codigoPermiso) => codigoPermiso !== item.codigo),
                          )
                        }}
                      />
                    ))}
                  </fieldset>
                )
              })}
            </div>

            {sucio && (
              <div className="sigfisc-acciones">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBorrador(undefined)
                  }}
                >
                  Descartar los cambios
                </Button>
                <Button
                  variant="primary"
                  loading={guardarPermisos.isPending}
                  onClick={() => {
                    guardarPermisos.mutate(
                      { codigo: rol.codigo, permisos: actuales },
                      {
                        onSuccess: (respuesta) => {
                          setBorrador(undefined)
                          avisarExito(
                            'Matriz guardada.',
                            `Afecta a ${plural(respuesta.cuentasAfectadas, 'cuenta activa', 'cuentas activas')}.`,
                          )
                        },
                        onError: (error) => {
                          avisarError(error, 'No se pudo guardar la matriz')
                        },
                      },
                    )
                  }}
                >
                  Guardar la matriz
                </Button>
              </div>
            )}
          </>
        )}

        <Dialog
          open={creando}
          onOpenChange={(valor) => {
            if (!valor) {
              setCodigo('')
              setNombre('')
              setDescripcion('')
            }
            setCreando(valor)
          }}
        >
          <DialogContent>
            <DialogTitle>Nuevo rol</DialogTitle>
            <DialogDescription>
              Nace sin permisos. Se le asignan marcando casillas en la matriz.
            </DialogDescription>

            <form
              className="sigfisc-formulario"
              onSubmit={(evento) => {
                evento.preventDefault()
                crearRol.mutate(
                  { codigo: codigo.trim(), nombre: nombre.trim(), descripcion: descripcion.trim() },
                  {
                    onSuccess: () => {
                      avisarExito('Rol creado.')
                      setRolActivo(codigo.trim())
                      setBorrador(undefined)
                      setCreando(false)
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo crear el rol')
                    },
                  },
                )
              }}
            >
              <FormField
                id="rol-codigo"
                label="Código"
                required
                hint="Minúsculas y guiones bajos, entre 3 y 40 caracteres."
              >
                <Input
                  value={codigo}
                  autoFocus
                  onChange={(evento) => {
                    setCodigo(evento.target.value)
                  }}
                />
              </FormField>

              <FormField id="rol-nombre" label="Nombre" required>
                <Input
                  value={nombre}
                  onChange={(evento) => {
                    setNombre(evento.target.value)
                  }}
                />
              </FormField>

              <FormField id="rol-descripcion" label="Descripción">
                <Textarea
                  rows={2}
                  value={descripcion}
                  onChange={(evento) => {
                    setDescripcion(evento.target.value)
                  }}
                />
              </FormField>

              <div className="sigfisc-dialogo__acciones">
                <DialogClose asChild>
                  <Button variant="secondary" type="button">
                    Cancelar
                  </Button>
                </DialogClose>
                <Button
                  variant="primary"
                  type="submit"
                  loading={crearRol.isPending}
                  disabled={codigo.trim() === '' || nombre.trim() === ''}
                >
                  Crear el rol
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
